import {reportBug} from '@packages/utilities/reporter';
import {
  Component,
  ExpressionPath,
  Expressions,
  Plugins,
} from '@packages/systems/wfdl';
import {
  EList,
  Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  CHILDREN_PATH,
  updateComponentRenderInComponentMap,
} from '@packages/utilities/platform';
import {isCell} from '@packages/utilities/elementTypeUtils';
import {instantiateExpressionWithUniqueIds} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {generateGuid} from '@packages/utilities/guid';
import {getSecond} from '@packages/utilities/Pair';

const STYLEBLOCK_IDS_PATH: ExpressionPath = [
  {in: 'Record', at: 'styleBlockIds'},
];

const reportError = (reason: string) => {
  reportBug(
    'LayoutError',
    `Failed to paste cell children and styles: ${reason}`,
    {
      team: 'structure',
      ticket: 'https://webflow.atlassian.net/browse/ACT-1851',
      method: 'pasteCellInComponentMap',
    }
  );
};

const duplicateChild = (child: Expression) =>
  getSecond(instantiateExpressionWithUniqueIds(child)(generateGuid()));

type PasteCellChildrenAndStylesRenderOpts = {
  cellElementId: string;
  pastedElement: ExpressionElement;
  componentMap: Component.ComponentMap;
  componentType: Plugins.QualifiedElement;
};

/**
 * Given a Cell element id and pasted cell element id (with needed component map dependencies), it
 * replaces the children and styles with those of pasted cell element
 */
const pasteCellInComponentMap = ({
  cellElementId,
  pastedElement,
  componentMap,
  componentType,
}: PasteCellChildrenAndStylesRenderOpts): Component.ComponentMap => {
  const component = componentMap.get(componentType);
  if (!component) {
    // Something went wrong, let's abort and return the original componentMap
    reportError('Component not found');
    return componentMap;
  }
  const componentRender = Component.getRender(component);

  if (!isCell(pastedElement)) {
    reportError('Pasted element is not Cell');
    return componentMap;
  }

  const updateCellElement = (cellElement: ExpressionElement) => {
    if (!isCell(cellElement)) {
      reportError('Element is not Cell');
      return cellElement;
    }
    // get element data
    const dataExpr = pastedElement
      ? Expressions.getElementData(pastedElement)
      : null;
    // get pastedElement children as a list
    const childrenExpr = dataExpr
      ? Expressions.getIn(dataExpr, CHILDREN_PATH)
      : null;
    const childrenList = childrenExpr
      ? Expressions.getList(childrenExpr)
      : null;

    // create clones of children with new IDs
    const duplicatedChildrenList = childrenList
      ? childrenList.map(duplicateChild)
      : [];

    // get pastedElement's styleBlockIds as a list
    const styleBlockIdsExpr = dataExpr
      ? Expressions.getIn(dataExpr, STYLEBLOCK_IDS_PATH)
      : null;
    const styleBlockIdsList = styleBlockIdsExpr
      ? Expressions.getList(styleBlockIdsExpr)
      : null;

    let nextElementData = Expressions.getElementData(cellElement);
    if (duplicatedChildrenList) {
      nextElementData = Expressions.setIn(
        nextElementData,
        CHILDREN_PATH,
        EList(duplicatedChildrenList)
      );
    }

    if (styleBlockIdsList) {
      nextElementData = Expressions.sparseUpdateIn(
        nextElementData,
        STYLEBLOCK_IDS_PATH,
        () => EList(styleBlockIdsList)
      );
    }

    return Expressions.setElementData(cellElement, nextElementData);
  };

  const updatedRenderForComponent = Expressions.updateElement(
    cellElementId,
    updateCellElement,
    componentRender
  );

  if (updatedRenderForComponent === componentRender) {
    // we did not update render, so no need to update the component map either
    return componentMap;
  }

  return updateComponentRenderInComponentMap(
    componentMap,
    componentType,
    updatedRenderForComponent
  );
};

export default pasteCellInComponentMap;
